import { APIExportStartReturnType } from 'src/models/BaseApi';
import { FaviconRequestType, FaviconResponseType } from 'src/models/common';
import { DonationPageTableResponseType } from 'src/models/DonationPage/List';
import { InviteUsersModalRequestType, InviteUsersModalResponseType } from 'src/models/Modals/InviteUsers';
import {
  OrganizationOldVerifyDeleteFileRequestType,
  OrganizationOldVerifyDeleteFileResponseType,
  OrganizationOldVerifyPageSaveRequestType,
  OrganizationOldVerifyPageSaveResponseType,
  OrganizationOldVerifyPageLoadRequestType,
} from 'src/models/Organization/OldVerify';
import { OrganizationBasicsResponseType, OrganizationSaveBasicsRequestType } from 'src/models/Organization/SetupBasics';
import {
  OrganizationSetupBrandingCheckSMTPType,
  OrganizationSetupBrandingGetBrandedUrlsInUseRequestType,
  OrganizationSetupBrandingGetBrandedUrlsInUseResponseType,
  SetupBrandingResponseType,
} from 'src/models/Organization/SetupBranding';
import {
  OrganizationStaffInviteAdvocateRequestType,
  OrganizationStaffInviteAdvocateResponseType,
  OrganizationStaffListType,
} from 'src/models/Organization/Staff';
import { SettingsStaffSaveUserRequestType, SettingsStaffSaveUserResponseType } from 'src/models/Settings/Staff';
import { CommonTableRequestType } from 'src/models/Table';
import { OrganizationSetupBrandingFormValues } from 'src/pages/Organization/SetupBranding/form';
import { ApiFunctionType, httpGet, httpGetCancellable, httpPost, httpPostCancellable } from 'src/utils/http';
import { OrganizationResentAdministratorInvitationResponseType } from 'src/models/Organization';
import { OrganizationProfileRequestType, OrganizationProfileResponseType } from 'src/models/Organization/Profile';
import {
  OrganizationSetupHomeResponseType,
  OrganizationSetupHomeSaveFormRequestType,
  OrganizationValidateUrlRequestType,
} from 'src/models/Organization/SetupHome';
import {
  OrganizationIntegrationsLoadCustomFieldsRequestType,
  OrganizationIntegrationsLoadCustomFieldsResponseType,
  OrganizationIntegrationsResponseType,
} from 'src/models/Organization/Integrations';
import {
  OrganizationSetupFundingRequestType,
  OrganizationSetupFundingResponseType,
} from 'src/models/Organization/SetupFunding';
import {
  OrganizationInternalProcessingRequestReasonRequestType,
  OrganizationInternalProcessingRequestReasonResponseType,
} from 'src/models/Organization/internalProcessingRequest';
import {
  OrganizationSavePricingRequestType,
  OrganizationSavePricingResponseType,
  OrganizationSetupPricingResponseType,
} from 'src/models/Organization/pricing';

const getPath = (str: string) => `/organization/${str}`;

/** get donation page list */
export const apiOrganizerGetDonationPageList: ApiFunctionType<CommonTableRequestType, DonationPageTableResponseType> =
  httpPostCancellable(getPath('loadDonationPages'));

export const apiDonationPagesExport = (id: string): Promise<APIExportStartReturnType> =>
  httpPost(getPath('donationpagescsv'), { permanentUrl: id });

/** resend administrator invitation */
export const apiResentAdministratorInvitation: ApiFunctionType<
  { OrganizationUserID: number },
  OrganizationResentAdministratorInvitationResponseType
> = httpPostCancellable(getPath('resendadministratorinvitation'));

/** remove administrator */
export const apiRemoveAdministrator: ApiFunctionType<
  { OrganizationUserID: number },
  OrganizationResentAdministratorInvitationResponseType
> = httpPostCancellable(getPath('removeadministrator'));

/** generate a favicon */
export const apiGenerateFavicon = (data: FaviconRequestType): Promise<FaviconResponseType> =>
  httpPost(getPath('generatefavicon'), data);

/** save organization profile */
export const apiOrganizationSaveProfile: ApiFunctionType<
  OrganizationProfileRequestType,
  OrganizationProfileResponseType
> = httpPostCancellable(getPath('saveprofile'));

/** validate organization friendly url */
export const apiOrganizationValidateFriendlyUrl: ApiFunctionType<
  OrganizationValidateUrlRequestType,
  OrganizationProfileResponseType
> = httpPostCancellable(getPath('validateorganizationfriendlyurl'));

/** organization save home page */
export const apiOrganizationSaveHomePage = (
  data: OrganizationSetupHomeSaveFormRequestType
): Promise<OrganizationSetupHomeSaveFormRequestType> => httpPost(getPath('savehomepage'), data);

/** get organization setup home model */
export const apiGetOrganizationSetupHomePage: ApiFunctionType<{ id: string }, OrganizationSetupHomeResponseType> =
  httpGetCancellable(getPath('getorganizationsetuphomepage'));

/** load active campaign custom fields */
export const apiLoadActiveCampaignCustomFields: ApiFunctionType<
  OrganizationIntegrationsLoadCustomFieldsRequestType,
  OrganizationIntegrationsLoadCustomFieldsResponseType
> = httpPostCancellable(getPath('loadactivecampaigncustomfields'));

/** organization setup basics */
export const apiGetOrganizationBasicsPageMockData: ApiFunctionType<{ id?: string }, OrganizationBasicsResponseType> =
  httpGetCancellable(getPath('getsetupbasics'));

/** save organization integrations */
export const apiSaveBasics: ApiFunctionType<OrganizationSaveBasicsRequestType, OrganizationBasicsResponseType> =
  httpPostCancellable(getPath('savebasics'));

export const apiSaveFunding: ApiFunctionType<OrganizationSetupFundingRequestType, OrganizationSetupFundingRequestType> =
  httpPostCancellable(getPath('savefunding'));

export const apiLoadDefaultProcessingRates = (
  currency: string
): Promise<OrganizationSetupFundingResponseType['OrganizationPricing']> =>
  httpPost(getPath('loaddefaultprocessingrates'), { currency });

export const apiGetOrganizationIntegrations = (id?: string): Promise<OrganizationIntegrationsResponseType> =>
  httpGet(getPath('getorganizationintegrations'), { id });

/** organization setup profile */
export const apiGetOrganizationProfile: ApiFunctionType<{ id: string }, OrganizationProfileResponseType> =
  httpGetCancellable(getPath('getOrganizationProfile'));

/** --- staff/users --- */
export const apiGetOrganizationStaffListData: ApiFunctionType<
  CommonTableRequestType & { RequestParams: { PermanentUrl: string } },
  OrganizationStaffListType
> = httpPostCancellable(getPath('loadStaffs'));

/** invite users */
export const apiOrganizationStaffInvitePeople = (
  model: InviteUsersModalRequestType
): Promise<InviteUsersModalResponseType> => httpPost(getPath('inviteadministrators'), model);

/** invite advocate */
export const apiOrganizationStaffInviteAdvocate: ApiFunctionType<
  OrganizationStaffInviteAdvocateRequestType,
  OrganizationStaffInviteAdvocateResponseType
> = httpPostCancellable(getPath('inviteAdvocate'));

/** export csv */
export const apiOrganizationStaffExport = (permanentUrl: string): Promise<APIExportStartReturnType> =>
  httpPost(getPath('staffscsv'), {
    permanentUrl,
  });

/** resend administrator invitation */
export const apiOrganizationResendAdministratorInvitation: ApiFunctionType<{ OrganizationUserID: number }, unknown> =
  httpPostCancellable(getPath('resendAdministratorInvitation'));

/** resend advocate invitation */
export const apiOrganizationResendAdvocateInvitation: ApiFunctionType<
  {
    ID: number;
  },
  unknown
> = httpPostCancellable(getPath('resendAdvocateInvitation'));

/** remove a user */
export const apiOrganizationRemoveUser: ApiFunctionType<
  {
    OrganizationUserID: number;
  },
  unknown
> = httpPostCancellable(getPath('removeAdministrator'));

/** remove an advocate */
export const apiOrganizationRemoveAdvocate: ApiFunctionType<
  {
    ID: number;
  },
  unknown
> = httpPostCancellable(getPath('removeAdvocate'));

/** save a user */
export const apiOrganizationSaveUser: ApiFunctionType<
  SettingsStaffSaveUserRequestType,
  SettingsStaffSaveUserResponseType
> = httpPostCancellable(getPath('saveAdministrator'));
/** --- end staff/users --- */

export const apiOrganizationGetSetupFundingData: ApiFunctionType<
  { id?: string },
  OrganizationSetupFundingResponseType
> = httpGetCancellable(getPath('getsetupfundingdata'));

/** validate a custom subdomain */
export const apiOrganizationValidateCustomSubdomain = (
  data: SetupBrandingResponseType
): Promise<SetupBrandingResponseType> => httpPost(getPath('validatecustomsubdomain'), data);

/** validate a cname */
export const apiOrganizationValidateCname = (data: SetupBrandingResponseType): Promise<SetupBrandingResponseType> =>
  httpPost(getPath('validatecname'), data);

/** validate SMTP data */
export const apiOrganizationTestSMTP = (
  data: OrganizationSetupBrandingCheckSMTPType
): Promise<OrganizationSetupBrandingCheckSMTPType> => httpPost(getPath('testsmtpconnection'), data);

/** check if there are branded urls in use */
export const apiOrganizationGetBrandedUrlsInUse: ApiFunctionType<
  OrganizationSetupBrandingGetBrandedUrlsInUseRequestType,
  OrganizationSetupBrandingGetBrandedUrlsInUseResponseType
> = httpPostCancellable(getPath('getBrandedUrlsInUse'));

/** save organization branding page data */
export const apiSaveOrganizationBranding: ApiFunctionType<
  OrganizationSetupBrandingFormValues,
  SetupBrandingResponseType
> = httpPostCancellable(getPath('saveBranding'));

/** data for organization branding page */
export const apiGetOrganizationBrandingData: ApiFunctionType<{ id: string }, SetupBrandingResponseType> =
  httpGetCancellable(getPath('getSetupBrandingViewModel'));

export const apiCreateOrganizationInternalProcessingRequest: ApiFunctionType<
  OrganizationInternalProcessingRequestReasonRequestType,
  OrganizationInternalProcessingRequestReasonResponseType
> = httpPostCancellable(getPath('CreateOrganizationInternalProcessingRequest'));

export const apiGetOrganizationVerifyData: ApiFunctionType<{ id: string }, OrganizationOldVerifyPageLoadRequestType> =
  httpGetCancellable(getPath('GetVerifyData'));

export const apiOrganizationDeleteVerificationAccountFile: ApiFunctionType<
  OrganizationOldVerifyDeleteFileRequestType,
  OrganizationOldVerifyDeleteFileResponseType
> = httpPostCancellable(getPath('deleteVerificationAccountFile'));

export const apiOrganizationSaveVerifyAccount: ApiFunctionType<
  OrganizationOldVerifyPageSaveRequestType,
  OrganizationOldVerifyPageSaveResponseType
> = httpPostCancellable(getPath('saveVerifyAccount'));

/** pricing */
export const apiGetOrganizationPricing: ApiFunctionType<
  { permanentUrl?: string },
  OrganizationSetupPricingResponseType
> = httpGetCancellable(getPath('GetOrganizationPricing'));

export const apiSavePricing: ApiFunctionType<OrganizationSavePricingRequestType, OrganizationSavePricingResponseType> =
  httpPostCancellable(getPath('SavePricing'));
/** end pricing */
